import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, Lock } from "lucide-react";
import api from "@/lib/api";

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
export const fmtDate = (iso, lang = "fr") => new Date(iso).toLocaleDateString(lang === "en" ? "en-GB" : "fr-FR", { day: "numeric", month: "long" });

// Kid pages: countdown during the 10-min trial, lock screen when it is over
export default function TrialGate() {
  const { childId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [st, setSt] = useState(null);
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!childId) return undefined;
    const load = () => api.get(`/children/${childId}/trial`).then(({ data }) => { setSt(data); setLeft(data.remaining_s); }).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [childId, pathname]);
  useEffect(() => {
    if (st?.state !== "active") return undefined;
    const id = setInterval(() => setLeft((s) => Math.max(0, (s ?? 0) - 1)), 1000);
    return () => clearInterval(id);
  }, [st?.state]);
  if (!st || pathname.endsWith("/demo")) return null;
  const ended = st.state === "ended" || (st.state === "active" && left === 0);
  if (st.state === "active" && !ended) {
    return (
      <div className={`fixed bottom-4 right-4 z-[55] rounded-full border-[3px] border-slate-900 px-4 py-2 font-kids font-black shadow-[4px_4px_0_0_#111] flex items-center gap-2 ${left < 300 ? "bg-red-300" : "bg-[#B7F000]"}`} data-testid="trial-countdown">
        <Clock size={18} /> <span className="tabular-nums" data-testid="trial-countdown-time">{mmss(left)}</span>
        <span className="font-parent text-xs font-bold hidden sm:inline">essai gratuit · free trial</span>
      </div>
    );
  }
  if (!ended) return null;
  return (
    <div className="fixed inset-0 z-[58] bg-[#0F172A]/80 backdrop-blur-md flex items-center justify-center p-5" data-testid="trial-ended-lock">
      <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg w-full bg-[#FDFCF0] border-4 border-slate-900 rounded-[2rem] shadow-[10px_10px_0_0_#111] p-7 text-center font-parent">
        <img src="/mascot/kiko.png" alt="Kiko" className="w-24 mx-auto" />
        <h2 className="font-kids text-2xl sm:text-3xl font-black mt-2 flex items-center justify-center gap-2"><Lock size={24} /> L'essai est terminé&nbsp;!</h2>
        <p className="font-kids text-lg font-bold text-slate-600">Your free trial is over!</p>
        <p className="mt-4">Bravo {st.child_name} ! Demande à tes parents de choisir une formule pour continuer.</p>
        {!st.reset && st.keep_until && (
          <p className="mt-3 rounded-2xl bg-yellow-200 border-2 border-slate-900 p-3 text-sm font-semibold" data-testid="trial-ended-keep">
            Parents : payez avant le {fmtDate(st.keep_until)} et {st.child_name} garde toute sa progression. / Pay before {fmtDate(st.keep_until, "en")} to keep the progress.
          </p>
        )}
        <button onClick={() => navigate(`/pricing?trial_ended=${childId}`)} data-testid="trial-ended-plans"
          className="mt-6 w-full rounded-full border-[3px] border-slate-900 bg-[#B7F000] px-4 py-4 font-kids font-black text-base sm:text-xl shadow-[4px_4px_0_0_#111] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#111] transition-transform transition-shadow">
          Choisir une formule<span className="block sm:inline text-sm sm:text-xl"><span className="hidden sm:inline"> · </span>Choose a plan</span>
        </button>
        <button onClick={() => navigate("/parent")} className="mt-3 text-sm font-semibold underline" data-testid="trial-ended-parent">Espace parent · Parent space</button>
      </motion.div>
    </div>
  );
}
